import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { OccurrenceResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { calendarApi } from '@/services/calendar.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, Spinner } from '@/components/ui';
import { CalendarGrid } from './calendar-grid';
import { CalendarSidebar } from './calendar-sidebar';
import { ManageCalendarsDialog } from './manage-calendars-dialog';
import { CalendarEventDialog, type CalendarEventDialogMode } from './calendar-event-dialog';
import { addMonthsUTC, gridRange, startOfMonthUTC } from './calendar-dates';

const HIDDEN_CALENDARS_KEY = 'tabita.calendar.hiddenCalendars';

function loadHidden(): Set<string> {
  try {
    const raw = window.localStorage.getItem(HIDDEN_CALENDARS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveHidden(ids: Set<string>): void {
  try {
    window.localStorage.setItem(HIDDEN_CALENDARS_KEY, JSON.stringify([...ids]));
  } catch {
    // A private window or full storage just means the preference doesn't
    // survive this visit — not a broken page.
  }
}

/**
 * The Calendar module: a month grid with a sidebar of toggleable,
 * color-coded calendars, aggregating this tenant's own scheduled entries
 * with published Events and Group weekly meeting times.
 */
export function CalendarPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const [viewDate, setViewDate] = useState(() => startOfMonthUTC(new Date(Date.now())));
  const [hiddenCalendarIds, setHiddenCalendarIds] = useState(loadHidden);
  const [manageOpen, setManageOpen] = useState(false);
  const [eventDialog, setEventDialog] = useState<CalendarEventDialogMode | null>(null);

  const range = useMemo(() => gridRange(viewDate), [viewDate]);

  const showArchivedCalendars = getShowArchivedRecordsPreference();
  const calendarsQuery = useQuery({
    queryKey: ['calendar', 'calendars', showArchivedCalendars],
    queryFn: ({ signal }) => calendarApi.listCalendars(showArchivedCalendars, signal),
  });

  const occurrencesQuery = useQuery({
    queryKey: ['calendar', 'occurrences', range.from, range.to],
    queryFn: ({ signal }) => calendarApi.listOccurrences(range.from, range.to, signal),
  });

  const toggleCalendar = (calendarId: string) => {
    setHiddenCalendarIds((prev) => {
      const next = new Set(prev);
      if (next.has(calendarId)) next.delete(calendarId);
      else next.add(calendarId);
      saveHidden(next);
      return next;
    });
  };

  const canManage = can('calendar.calendar.create');
  const canAddEvent = can('calendar.event.create');
  const canEditEvent = can('calendar.event.update');

  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(viewDate);

  const visibleOccurrences = (occurrencesQuery.data ?? []).filter(
    (occurrence) => !hiddenCalendarIds.has(occurrence.calendarId),
  );

  const handleSelectOccurrence = (occurrence: OccurrenceResponse) => {
    if (occurrence.source === 'EVENT') {
      void navigate(`/events/${occurrence.sourceId}`);
      return;
    }
    if (occurrence.source === 'GROUP') {
      void navigate(`/groups/${occurrence.sourceId}`);
      return;
    }
    if (canEditEvent) setEventDialog({ mode: 'edit', eventId: occurrence.sourceId });
  };

  const loadError = calendarsQuery.error ?? occurrencesQuery.error;

  return (
    <div className="page calendar-page">
      <header className="page-header">
        <h1 className="page-title">{t('calendar.title')}</h1>
        <div className="calendar-toolbar">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setViewDate(startOfMonthUTC(new Date(Date.now())))}
          >
            {t('calendar.today')}
          </Button>
          <div className="calendar-nav">
            <button
              type="button"
              className="calendar-nav-btn"
              aria-label={t('calendar.previousMonth')}
              onClick={() => setViewDate((d) => addMonthsUTC(d, -1))}
            >
              ‹
            </button>
            <span className="calendar-month-label">{monthLabel}</span>
            <button
              type="button"
              className="calendar-nav-btn"
              aria-label={t('calendar.nextMonth')}
              onClick={() => setViewDate((d) => addMonthsUTC(d, 1))}
            >
              ›
            </button>
          </div>
          <Link
            to={`/calendar/print?from=${range.from}&to=${range.to}&month=${encodeURIComponent(monthLabel)}`}
            className="btn"
          >
            {t('common.actions.print')}
          </Link>
        </div>
      </header>

      {loadError && (
        <Alert tone="danger">
          {loadError instanceof ApiError
            ? t(`errors.${loadError.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      <div className="calendar-layout">
        <CalendarSidebar
          calendars={calendarsQuery.data ?? []}
          occurrences={occurrencesQuery.data ?? []}
          hiddenCalendarIds={hiddenCalendarIds}
          onToggle={toggleCalendar}
          onAddEvent={canAddEvent ? () => setEventDialog({ mode: 'create' }) : undefined}
          onManage={canManage ? () => setManageOpen(true) : undefined}
        />

        <div className="calendar-main">
          {occurrencesQuery.isPending ? (
            <Spinner label={t('common.states.loading')} />
          ) : (
            <CalendarGrid
              viewDate={viewDate}
              occurrences={visibleOccurrences}
              locale={locale}
              onSelectOccurrence={handleSelectOccurrence}
            />
          )}
        </div>
      </div>

      {manageOpen && (
        <ManageCalendarsDialog
          calendars={calendarsQuery.data ?? []}
          onClose={() => setManageOpen(false)}
        />
      )}

      {eventDialog && (
        <CalendarEventDialog
          calendars={calendarsQuery.data ?? []}
          mode={eventDialog}
          onClose={() => setEventDialog(null)}
        />
      )}
    </div>
  );
}
